import { useEffect, useRef, useState, type RefObject } from 'react';
import type { Size } from '@sorrem/shared';
import { MAX_FRAME_SECONDS } from './skill-tree-canvas';

/**
 * Drives a canvas layer that fills `containerRef`: tracks the container's
 * size (returned, in CSS pixels), and while `active` and sized, calls
 * `onFrame` every animation frame with the seconds since the last one
 * (capped at `MAX_FRAME_SECONDS`) and the current size. `onFrame` may
 * change every render; each frame calls the latest.
 */
export function useCanvasFrameLoop(
    containerRef: RefObject<HTMLElement>,
    isActive: boolean,
    onFrame: (deltaSeconds: number, viewport: Size) => void,
): Size {
    const [viewport, setViewport] = useState<Size>({ width: 0, height: 0 });
    const onFrameRef = useRef(onFrame);
    onFrameRef.current = onFrame;

    // Track the layer's size; the canvas fills it.
    useEffect(() => {
        const container = containerRef.current;
        if (!container) return;
        const measure = (): void => setViewport({ width: container.clientWidth, height: container.clientHeight });
        measure();
        const observer = new ResizeObserver(measure);
        observer.observe(container);
        return () => observer.disconnect();
    }, [containerRef]);

    useEffect(() => {
        if (viewport.width === 0 || !isActive) return;
        let frame = 0;
        let last = performance.now();
        const tick = (now: number): void => {
            const deltaSeconds = Math.min(MAX_FRAME_SECONDS, (now - last) / 1000);
            last = now;
            onFrameRef.current(deltaSeconds, viewport);
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [isActive, viewport]);

    return viewport;
}
